// runOutcome.js — o resultado CANÔNICO e imutável de qualquer execução (turno único,
// DAG, self-repair). Hoje o "resultado" vive em ~5 formatos paralelos que quase batem
// (planRunHonestlyDone boolean, blockerTaxonomy.BLOCKER, dagOutcome.reachedGreen,
// greenPassFromEvents 1|0|null, loop stopReason). Este é o tipo ÚNICO que a UI, o plano
// e o learning passam a ler — nenhum deriva o seu. Bridges de cada formato legado ficam
// em ./fromExisting.js (consolidação, não reescrita). @arch:pure-engine

/** @typedef {'verified'|'implemented_unverified'|'blocked'|'failed'|'cancelled'|'environment_unavailable'} RunState */

export const RUN = Object.freeze({
  /** oráculo verde E rodou um verificador REAL — a única coisa que conclui um plano. */
  VERIFIED: 'verified',
  /** mudanças entraram no disco, mas nenhum verificador executável casou (não é verde silencioso — fica marcado). */
  IMPLEMENTED_UNVERIFIED: 'implemented_unverified',
  /** stop recuperável (permission/timeout/aborto-de-loop/context/needs_user) — pausa, não conclui, não envenena o learning. */
  BLOCKED: 'blocked',
  /** um verificador RODOU e reprovou (vermelho honesto). */
  FAILED: 'failed',
  /** o usuário abortou (Stop). */
  CANCELLED: 'cancelled',
  /** faltou dependência/ferramenta pra verificar (worktree sem node_modules etc.) — pausa acionável. */
  ENVIRONMENT_UNAVAILABLE: 'environment_unavailable',
});

const ALL_STATES = new Set(Object.values(RUN));

// Sets internos (a API pública são os helpers canComplete/isStop — ninguém importa os
// sets crus). Manter privados evita export morto (knip) sem perder a intenção.
/** Estados que PODEM concluir um plano. SÓ verified fecha verde; unverified fecha marcado. */
const COMPLETABLE = new Set([RUN.VERIFIED, RUN.IMPLEMENTED_UNVERIFIED]);
/** Estados que PAUSAM (nunca concluem): o usuário precisa agir. */
const STOP_STATES = new Set([RUN.BLOCKED, RUN.ENVIRONMENT_UNAVAILABLE]);

/**
 * @typedef {Object} RunOutcome
 * @property {RunState} state
 * @property {boolean}  verifierRan   rodou ALGUM verificador executável?
 * @property {string|null} reason     razão-máquina (stopReason bruto: task_timeout, no_root, dag_done…)
 * @property {string|null} detail     1 frase humana (pra UI)
 * @property {ReadonlyArray<{id:string,label:string}>} actions  affordances (instalar deps, ver comandos…)
 * @property {object} evidence        {verifiers?, model?, provider?, taskId?}
 */

/**
 * Constrói um RunOutcome imutável. Estado inválido → cai em BLOCKED (nunca inventa verde).
 * @param {Partial<RunOutcome> & {state: RunState}} o
 * @returns {Readonly<RunOutcome>}
 */
export function makeOutcome(o) {
  const state = ALL_STATES.has(o?.state) ? o.state : RUN.BLOCKED;
  return Object.freeze({
    state,
    verifierRan: !!o.verifierRan,
    reason: o.reason != null ? String(o.reason) : null,
    detail: o.detail != null ? String(o.detail) : null,
    actions: Object.freeze(Array.isArray(o.actions) ? o.actions.map((a) => Object.freeze({ ...a })) : []),
    evidence: Object.freeze({ ...(o.evidence || {}) }),
  });
}

export const isVerified = (o) => !!o && o.state === RUN.VERIFIED;
export const canComplete = (o) => !!o && COMPLETABLE.has(o.state);
export const isStop = (o) => !!o && STOP_STATES.has(o.state);
